/**
 * The four exercise faces, for either grammar course.
 *
 * One exercise is one card and one boolean, judged the way Konjugation judges
 * its eight fields: right on the first try, with no reveal. That keeps
 * `memorized` meaning something, and unlike Konjugation it needs no
 * `attempts.detail` at all — there is only ever one thing to get right.
 *
 * Every face reports through the same `onAnswer`, and every face offers
 * "I don't know". That is not decoration: a card the user cannot answer has two
 * outcomes, an honest blank or a guess, and a guess writes a wrong answer into
 * `attempts.user_answer` that the mistakes list then reports as a misconception
 * the user never held.
 */
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  acceptedAnswers,
  BLANK,
  readingOrder,
  type AuxText,
  type GrammarCourse,
  type GrammarExercise,
} from './types';

type Props = {
  exercise: GrammarExercise;
  course: GrammarCourse;
  primaryLang?: string;
  /** Remounts the faces when the card changes, resetting their local state. */
  cardKey: string | number;
  onAnswer: (correct: boolean, userAnswer: string | null) => void;
};

export function GrammarCardFront({ exercise, course, primaryLang, cardKey, onAnswer }: Props) {
  const shared = { course, primaryLang, onAnswer };

  return (
    <div className="border-border bg-card flex flex-col gap-8 rounded-2xl border p-6 shadow-sm sm:p-8">
      {exercise.kind === 'fill_blank' && (
        <FillBlank key={cardKey} exercise={exercise} {...shared} />
      )}
      {exercise.kind === 'choice' && <Choice key={cardKey} exercise={exercise} {...shared} />}
      {exercise.kind === 'transform' && <Transform key={cardKey} exercise={exercise} {...shared} />}
      {exercise.kind === 'order' && <Order key={cardKey} exercise={exercise} {...shared} />}

      <button
        type="button"
        onClick={() => onAnswer(false, null)}
        className="text-muted-foreground mx-auto text-sm underline-offset-4 hover:underline"
      >
        I don&apos;t know
      </button>
    </div>
  );
}

type FaceProps<K extends GrammarExercise['kind']> = {
  exercise: Extract<GrammarExercise, { kind: K }>;
  course: GrammarCourse;
  primaryLang?: string;
  onAnswer: (correct: boolean, userAnswer: string | null) => void;
};

// ---------------------------------------------------------------------------

/**
 * The prompt with its blank rendered as a visible gap rather than underscores.
 *
 * A prompt without a blank renders as plain text. Only `fill_blank` is required
 * by the schema to contain one; a `choice` may be a bare word the options
 * describe — "carro: masculino or feminino?" — and drawing an empty gap after
 * it invited the learner to type into something that is not there.
 */
function PromptWithBlank({ prompt, filled }: { prompt: string; filled?: string }) {
  const [before, after] = prompt.split(BLANK);
  if (after === undefined) {
    return <p className="font-display text-2xl leading-relaxed sm:text-3xl">{before}</p>;
  }
  return (
    <p className="font-display text-2xl leading-relaxed sm:text-3xl">
      {before}
      <span
        className={cn(
          'border-primary mx-1 inline-block min-w-16 border-b-2 pb-0.5 text-center align-baseline',
          !filled && 'text-transparent',
        )}
      >
        {filled || ' '}
      </span>
      {after}
    </p>
  );
}

function FillBlank({ exercise, course, primaryLang, onAnswer }: FaceProps<'fill_blank'>) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    if (!value.trim()) return;
    onAnswer(
      course.isAnswerCorrect(value, acceptedAnswers(exercise), exercise.strict),
      value.trim(),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <PromptWithBlank prompt={exercise.prompt} filled={value} />
      <Explanations text={exercise.hint} course={course} primaryLang={primaryLang} muted />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-label="Your answer"
        className="border-border bg-background focus-visible:border-primary w-full rounded-lg border-2 px-4 py-3 text-center text-xl focus-visible:outline-none"
      />
      <PrimaryButton onClick={submit} disabled={!value.trim()}>
        Check
      </PrimaryButton>
    </div>
  );
}

function Choice({ exercise, course, primaryLang, onAnswer }: FaceProps<'choice'>) {
  return (
    <div className="flex flex-col gap-6">
      <Explanations text={exercise.instruction} course={course} primaryLang={primaryLang} muted />
      {exercise.prompt && <PromptWithBlank prompt={exercise.prompt} />}
      <div
        className={cn(
          'grid gap-3',
          exercise.options.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
        )}
      >
        {exercise.options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onAnswer(option === exercise.answer, option)}
            className="border-border bg-card hover:border-primary hover:text-primary focus-visible:ring-ring min-h-14 rounded-lg border-2 px-4 py-3 text-lg font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function Transform({ exercise, course, primaryLang, onAnswer }: FaceProps<'transform'>) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    if (!value.trim()) return;
    onAnswer(
      course.isAnswerCorrect(value, acceptedAnswers(exercise), exercise.strict),
      value.trim(),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="font-display text-2xl leading-relaxed sm:text-3xl">{exercise.given}</p>
      <Explanations text={exercise.instruction} course={course} primaryLang={primaryLang} />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        autoComplete="off"
        spellCheck={false}
        aria-label="Your rewritten sentence"
        className="border-border bg-background focus-visible:border-primary w-full rounded-lg border-2 px-4 py-3 text-lg focus-visible:outline-none"
      />
      <PrimaryButton onClick={submit} disabled={!value.trim()}>
        Check
      </PrimaryButton>
    </div>
  );
}

/**
 * Tap the chunks into order.
 *
 * Tap-to-append rather than drag: drag is hostile on a phone, and this is an
 * app used on a tram.
 *
 * Chunks are stored in sentence-MEDIAL form and the first one placed is
 * capitalised here, because reordering a sentence moves the capital: *ich muss
 * heute arbeiten* and *heute muss ich arbeiten* are the same four chunks with
 * the capital in a different place, and so are *eu vou amanhã* and *amanhã eu
 * vou*. Storing "Ich" instead would render a capital mid-sentence, which is
 * wrong in both languages and looks like an app bug — both seed verifiers
 * reject it for exactly that reason.
 */
function Order({ exercise, course, onAnswer }: FaceProps<'order'>) {
  const [placed, setPlaced] = useState<number[]>([]);
  const remaining = exercise.chunks.map((_, i) => i).filter((i) => !placed.includes(i));

  const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

  const submit = () => {
    const chosen = placed.map((i) => exercise.chunks[i]);
    onAnswer(
      course.isOrderCorrect(chosen, [exercise.answer, ...exercise.also_accept]),
      // Report the sentence as it was DISPLAYED, capital and all. The card back
      // echoes this when several orders are correct, and echoing the stored
      // sentence-medial chunks would show the user a lowercase sentence they
      // never built.
      chosen.map((chunk, i) => (i === 0 ? capitalise(chunk) : chunk)).join(' '),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground text-sm font-medium">Put the sentence in order</p>

      <div className="border-border flex min-h-16 flex-wrap items-center gap-2 rounded-lg border-2 border-dashed p-3">
        {placed.length === 0 && (
          <span className="text-muted-foreground px-1 text-sm">Tap the words below</span>
        )}
        {placed.map((chunkIndex, position) => (
          <button
            key={`${chunkIndex}-${position}`}
            type="button"
            onClick={() => setPlaced(placed.filter((_, p) => p !== position))}
            className="border-primary bg-primary/10 text-primary rounded-md border px-3 py-1.5 text-lg font-medium"
          >
            {position === 0 ? capitalise(exercise.chunks[chunkIndex]) : exercise.chunks[chunkIndex]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {remaining.map((chunkIndex) => (
          <button
            key={chunkIndex}
            type="button"
            onClick={() => setPlaced([...placed, chunkIndex])}
            className="border-border bg-card hover:border-primary focus-visible:ring-ring rounded-md border-2 px-3 py-1.5 text-lg transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            {exercise.chunks[chunkIndex]}
          </button>
        ))}
      </div>

      <PrimaryButton onClick={submit} disabled={remaining.length > 0}>
        Check
      </PrimaryButton>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A hint or instruction, in BOTH the languages the course explains itself in.
 *
 * The per-app versions of this printed `.en` and stopped, which meant a
 * Brazilian learning German read every instruction in her second-best language
 * while the Portuguese sat unread in the payload. Showing both is the whole
 * point of a course that explains itself twice.
 */
function Explanations({
  text,
  course,
  primaryLang,
  muted = false,
}: {
  text: AuxText | null;
  course: GrammarCourse;
  primaryLang?: string;
  muted?: boolean;
}) {
  const lines = readingOrder(text, course, primaryLang);
  if (lines.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      {lines.map((line, i) => (
        <p
          key={i}
          className={cn(
            'text-sm',
            i === 0
              ? muted
                ? 'text-muted-foreground font-medium'
                : 'text-primary font-medium'
              : 'text-muted-foreground',
          )}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-12 w-full rounded-lg px-4 font-medium transition-opacity focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}
